var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
/**
 * @module cercaliagl/layer/WMS
 */
import { createUid } from '../util';
import Servers from '../servers';
/**
 * @typedef {Object} WMSParameters
 * @property {string} service Service. Default `WMS`.
 * @property {string} version WMS Version. Default `1.1.1`.
 * @property {string} request Request. Default `GetMap`
 * @property {string} format Format. Default `image/png`.
 * @property {string} projection Projection. Default `EPSG:3857`.
 * @property {string} srs SRS. Default: `EPSG:3857`.
 * @property {string} transparent Transparent. Default `true`.
 * @property {string} layers Layers. Default `default`.
 * @property {string} width Width in pixels. Default: `1024`.
 * @property {string} height Height in pixels. Default `1024'
 * @property {string} startParamsCharacter Start params character. Default `&`.
 */
/**
 * @typedef {Object} Options
 * @property {string} layerId Layer id
 * @property {string} [attribution] Attribution HTML for this layer. This value is visible in {@link module:cercaliagl/control/Logo~Logo}.
 * @property {import("../Map").default} map Map.
 * @property {string|Array.<string>} url URL or arary of URLS of WMS Service.
 * @property {number} tileSize Tile size in pixels
 * @property {WMSParameters} params
 */
/**
 * @type {WMSParameters}
 */
var WMSBaseParams = {
    service: 'WMS',
    version: '1.1.1',
    request: 'GetMap',
    format: 'image/png',
    projection: 'EPSG:3857',
    srs: 'EPSG:3857',
    transparent: true,
    layers: 'default',
    width: 1024,
    height: 1024,
};
/**
 * @classdesc
 * Layer for tile data from WMS servers.
 *
 * Example usage:
 *
 * ```
 * const map = new cercaliagl.Map({
 *   target: 'map',
 *   center: new cercaliagl.LonLat(-3.6918373493421393, 40.41706664527052),
 *   zoom: 13,
 * });
 *
 * map.whenReady(() => {
 *   const servers = [
 *     'http://c1.cercalia.com/cgi-bin/mapserv?map=/usr/nexus/cercalia2/a9/barris_pg.map',
 *     'http://c2.cercalia.com/cgi-bin/mapserv?map=/usr/nexus/cercalia2/a9/barris_pg.map',
 *     'http://c4.cercalia.com/cgi-bin/mapserv?map=/usr/nexus/cercalia2/a9/barris_pg.map',
 *     'http://c6.cercalia.com/cgi-bin/mapserv?map=/usr/nexus/cercalia2/a9/barris_pg.map'
 *   ];
 *
 *   const wmsParams = {
 *     service: 'WMS',
 *     version: '1.1.1',
 *     request: 'GetMap',
 *     format: 'image/png',
 *     layers: 'default',
 *     srs: 'EPSG:3857',
 *     width: '1024',
 *     height: '1024',
 *     exceptions: 'application/vnd.ogc.se_inimage'
 *   };
 *   const wmsLayer = new cercaliagl.layer.WMS({
 *     url: servers,
 *     controls: [],
 *     params: wmsParams,
 *     tileSize: 1024
 *   });
 *   map.addLayer(wmsLayer);
 * });
 *
 * ```
 * @api
 */
var WMS = /** @class */ (function () {
    /**
     * @constructor
     * @param {Options} options Options
     */
    function WMS(options) {
        /**
         * @private
         * @type {string}
         */
        this.layerId_ = options.layerId || "WMS".concat(createUid());
        /**
         * @private
         * @type {string}
         */
        this.attribution_ = options.attribution || '';
        /**
         * @private
         * @type {number}
         */
        this.tileSize_ = options.tileSize ? options.tileSize : 1024;
        /**
         * @private
         * @type {Object.<string, string>}
         */
        this.params_ = options.params ? __assign(__assign({}, WMSBaseParams), options.params) : {};
        /**
         * @private
         * @type {import("../Map").default}
         */
        this.cercaliaMap_ = null;
        /**
         * @private
         * @type {import("maplibre-gl").Map}
         */
        this.map_ = null;
        /**
         * @private
         * @type {string}
         */
        this.label_ = options.label || this.layerId_;
        /**
         * @private
         * @type {string}
         */
        this.startParamsCharacter_ = options.startParamsCharacter || '&';
        /**
         * @private
         * @type {Array.<string>}
         */
        this.baseUrls_ = options.url ? (Array.isArray(options.url) ? options.url : [options.url]) : undefined;
        /**
         * @private
         * @type {Array.<string>}
         */
        this.urls_ = this.formatUrls_();
        /**
         * @private
         * @type {maplibregl.AnySourceImpl}
         */
        this.source_ = null;
    }
    /**
     * Get Layer id.
     * @return {string}
     * @api
     */
    WMS.prototype.getLayerId = function () {
        return this.layerId_;
    };
    /**
     * Map label used in layer switcher.
     * @return {string} Name
     * @api
     */
    WMS.prototype.getLabel = function () {
        return this.label_;
    };
    /**
     * Get attribution string HTML
     * @return {string}
     * @api
     */
    WMS.prototype.getAttribution = function () {
        return this.attribution_;
    };
    /**
     * Get layer params
     * @return {string}
     * @api
     */
    WMS.prototype.getParams = function () {
        return this.params_;
    };
    /**
     * @private
     * @return {Array.<string>}
     */
    WMS.prototype.formatUrls_ = function () {
        var _this = this;
        if (!this.baseUrls_) {
            console.error('No WMS URLs specified'); // eslint-disable-line no-console
            return null;
        }
        var existingParams = [];
        var params = [];
        Object.entries(this.params_).forEach(function (_a) {
            var key = _a[0], value = _a[1];
            var keylc = key.toLocaleLowerCase();
            if (existingParams.indexOf(keylc) === -1) {
                existingParams.push(keylc);
                params.push("".concat(keylc, "=").concat(value));
            }
        });
        var bboxEpsg = "bbox-".concat(this.params_.srs ? this.params_.srs.toLocaleLowerCase().replace(':', '-') : 'bbox-epsg-3857');
        return this.baseUrls_.map(function (baseUrl) { return "".concat(baseUrl).concat(_this.startParamsCharacter_).concat(params.join('&'), "&bbox={").concat(bboxEpsg, "}"); });
    };
    /**
     * @param {import("../Map").default|null} map Map or null
     */
    WMS.prototype.setMap = function (map) {
        if (map) {
            this.cercaliaMap_ = map;
            this.map_ = map.getMapboxglMap();
            this.map_.addSource(this.layerId_, {
                type: 'raster',
                tiles: this.urls_,
                tileSize: this.tileSize_,
                crossOrigin: 'anonymous',
            });
            this.source_ = this.map_.getSource(this.layerId_);
            this.map_.addLayer({
                id: this.layerId_,
                type: 'raster',
                source: this.layerId_,
                paint: {},
            });
        }
        else {
            this.map_.removeLayer(this.layerId_);
            this.map_.removeSource(this.layerId_);
        }
    };
    /**
     * Update WMS parameters and refresh WMS images loaded.
     * @param {WMSParameters} params Params
     * @api
     */
    WMS.prototype.updateParams = function (params) {
        this.params_ = __assign(__assign({}, this.params), params);
        this.urls_ = this.formatUrls_();
        this.refresh();
    };
    /**
     * Refresh WMS
     * @api
     */
    WMS.prototype.refresh = function () {
        var map = this.cercaliaMap_;
        this.setMap(null);
        this.setMap(map);
    };
    /**
     * @return {maplibregl.Style}
     */
    WMS.prototype.getMapboxStyle = function () {
        var _a;
        return {
            version: 8,
            attribution: this.attribution_,
            glyphs: Servers.glyphs,
            sources: (_a = {},
                _a[this.layerId_] = {
                    type: 'raster',
                    scheme: 'xyz',
                    tiles: this.urls_,
                    tileSize: this.tileSize_,
                    crossOrigin: 'anonymous',
                },
                _a.buildings3d = {
                    type: 'vector',
                    tiles: [Servers.buildings3d],
                    minzoom: 15,
                    maxzoom: 16,
                },
                _a['3dlandmarks'] = {
                    type: 'vector',
                    tiles: [Servers.landmarks3d],
                    minzoom: 12,
                    maxzoom: 12,
                },
                _a),
            layers: [
                {
                    id: this.layerId_,
                    source: this.layerId_,
                    type: 'raster',
                    minzoom: 2,
                    maxzoom: 22,
                },
                {
                    id: 'buildings3d',
                    type: 'fill-extrusion',
                    source: 'buildings3d',
                    'source-layer': 'building',
                    minzoom: 15,
                    layout: { visibility: 'none' },
                    paint: {
                        'fill-extrusion-color': 'rgba(187, 187, 187, 1)',
                        'fill-extrusion-opacity': 0.6,
                        'fill-extrusion-height': ['get', 'height'],
                        'fill-extrusion-base': ['get', 'min_height'],
                    },
                },
                {
                    id: '3dlandmarks',
                    type: 'fill',
                    source: '3dlandmarks',
                    'source-layer': '3dlandmarks',
                    minzoom: 15,
                    paint: {
                        'fill-color': '#088',
                        'fill-opacity': 0,
                    },
                    layout: {
                        visibility: 'none',
                    },
                },
            ],
        };
    };
    return WMS;
}());
export default WMS;
//# sourceMappingURL=WMS.js.map